import { tellUsAboutYourselfBillboardTemplate } from './models/tour-stops/tell-us-about-yourself-billboard-template';
import { welcomeToAccountManagerBillboardTemplate } from './models/tour-stops/welcome-to-account-manager-billboard-template';
import { allTasksCompleteBillboardTemplate } from './models/tour-stops/all-tasks-complete-billboard-template';
import {
  createTourStopNavPoi,
  TourItinerary,
  TourService,
  TourStopBillboard,
  TourStopPoi,
} from '@tmx/modus-operandi';
import { translate } from '@jsverse/transloco';

export function createWelcomeTourItinerary(tourService: TourService) {
  let setupChoice: '' | 'no' | 'yes' = '';

  return new TourItinerary(
    translate('accountManagerTour.welcomeTour.title'),
    [
      new TourStopBillboard(
        welcomeToAccountManagerBillboardTemplate,
        {
          title: translate('accountManagerTour.welcomeTour.title'),
          subtitle: translate('accountManagerTour.welcomeTour.subtitle'),
          feature1Emphasis: translate(
            'accountManagerTour.welcomeTour.feature1Emphasis'
          ),
          feature1Regular: translate(
            'accountManagerTour.welcomeTour.feature1Regular'
          ),
          feature2RegularPrefix: translate(
            'accountManagerTour.welcomeTour.feature2RegularPrefix'
          ),
          feature2Emphasis: translate(
            'accountManagerTour.welcomeTour.feature2Emphasis'
          ),
          feature2RegularSuffix: translate(
            'accountManagerTour.welcomeTour.feature2RegularSuffix'
          ),
          feature3Emphasis: translate(
            'accountManagerTour.welcomeTour.feature3Emphasis'
          ),
          feature3Regular: translate(
            'accountManagerTour.welcomeTour.feature3Regular'
          ),
        },
        {
          nextButton: {
            displayText: translate('accountManagerTour.welcomeTour.getStarted'),
            listener: () => {
              tourService.showTourProgressCard.set(true);
              tourService.nextStop();
            },
          },
        }
      ),
      new TourStopBillboard(
        tellUsAboutYourselfBillboardTemplate,
        {
          title: translate('accountManagerTour.welcomeTour.tellUsTitle'),
          setupQuestion: translate(
            'accountManagerTour.welcomeTour.setupQuestion'
          ),
          disclaimer: translate('accountManagerTour.welcomeTour.disclaimer'),
          imageAltText: translate(
            'accountManagerTour.welcomeTour.imageAltText'
          ),
        },
        {
          selectYesButton: {
            displayText: translate('accountManagerTour.welcomeTour.yes'),
            listener: ({ selectYesButton, selectNoButton, nextButton }) => {
              setupChoice = 'yes';
              selectYesButton.classList.add('active');
              selectNoButton.classList.remove('active');
              nextButton.removeAttribute('disabled');
            },
          },
          selectNoButton: {
            displayText: translate('accountManagerTour.welcomeTour.no'),
            listener: ({ selectYesButton, selectNoButton, nextButton }) => {
              setupChoice = 'no';
              selectYesButton.classList.remove('active');
              selectNoButton.classList.add('active');
              nextButton.removeAttribute('disabled');
            },
          },
          nextButton: {
            displayText: translate('accountManagerTour.welcomeTour.continue'),
            listener: () => {
              if (setupChoice === 'yes') {
                tourService.nextStop();
              } else if (setupChoice === 'no') {
                tourService.endTour();
              }
            },
          },
          skipButton: {
            displayText: translate('accountManagerTour.welcomeTour.skip'),
            listener: () => {
              tourService.endTour();
            },
          },
        }
      ),
      new TourStopPoi(
        'mop-tour-progress',
        translate('accountManagerTour.welcomeTour.onboardingTooltipTitle'),
        translate('accountManagerTour.welcomeTour.onboardingTooltipBody'),
        {
          nextButton: {
            displayText: translate('accountManagerTour.welcomeTour.gotIt'),
            listener: () => {
              tourService.nextItinerary();
            },
          },
        },
        { side: 'top', align: 'center' }
      ),
    ],
    false
  );
}

export function createAssetsTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.assetsTour.addAssetsTitle'),
    [
      createTourStopNavPoi(
        tourService,
        '/ams/assets',
        '[data-tour-poi="assetsNav"]',
        translate('accountManagerTour.assetsTour.navigateToAssetsTitle'),
        translate('accountManagerTour.assetsTour.navigateToAssetsBody'),
        translate('accountManagerTour.assetsTour.gotIt'),
        translate('accountManagerTour.assetsTour.skip')
      ),
      new TourStopPoi(
        '[data-tour-poi="addAssetButtons"]',
        translate('accountManagerTour.assetsTour.addAssetsTitle'),
        translate('accountManagerTour.assetsTour.addAssetsBody'),
        {
          nextButton: {
            displayText: translate('accountManagerTour.assetsTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.assetsTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
      new TourStopPoi(
        '[data-tour-poi="assignLicensesTab"]',
        translate('accountManagerTour.assetsTour.selectLicenseTitle'),
        translate('accountManagerTour.assetsTour.selectLicenseBody'),
        {
          nextButton: {
            displayText: translate('accountManagerTour.assetsTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.assetsTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
      new TourStopPoi(
        '[data-tour-poi="assetsDataTable"]',
        translate('accountManagerTour.assetsTour.selectAssetsTitle'),
        '',
        {
          nextButton: {
            displayText: translate('accountManagerTour.assetsTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.assetsTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
      new TourStopPoi(
        '[data-tour-poi="assetsAssignButton"]',
        translate('accountManagerTour.assetsTour.assignButtonTitle'),
        '',
        {
          nextButton: {
            displayText: translate('accountManagerTour.assetsTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.assetsTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
    ]
  );
}

export function createVehicleGroupTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.vehicleGroupsTour.itineraryTitle'),
    [
      createTourStopNavPoi(
        tourService,
        'ams/cfs/vehicles',
        '[data-tour-poi="cfsNav"]',
        translate('accountManagerTour.vehicleGroupsTour.navigateToCfsTitle'),
        translate('accountManagerTour.vehicleGroupsTour.navigateToCfsBody'),
        translate('accountManagerTour.vehicleGroupsTour.gotIt'),
        translate('accountManagerTour.vehicleGroupsTour.skip')
      ),
      createTourStopNavPoi(
        tourService,
        'ams/cfs/vehicle-groups',
        '[data-tour-poi="cfs/vehicle-groups"]',
        translate(
          'accountManagerTour.vehicleGroupsTour.navigateToVehicleGroupsTitle'
        ),
        translate(
          'accountManagerTour.vehicleGroupsTour.navigateToVehicleGroupsBody'
        ),
        translate('accountManagerTour.vehicleGroupsTour.gotIt'),
        translate('accountManagerTour.vehicleGroupsTour.skip')
      ),
      new TourStopPoi(
        '[data-tour-poi="addVehicleGroupButton"]',
        translate(
          'accountManagerTour.vehicleGroupsTour.createVehicleGroupTitle'
        ),
        translate(
          'accountManagerTour.vehicleGroupsTour.createVehicleGroupBody'
        ),
        {
          nextButton: {
            displayText: translate(
              'accountManagerTour.vehicleGroupsTour.gotIt'
            ),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.vehicleGroupsTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
    ]
  );
}

export function createRoutingProfileTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.routingProfilesTour.itineraryTitle'),
    [
      createTourStopNavPoi(
        tourService,
        'ams/cfs/routing-profiles',
        '[data-tour-poi="cfs/routing-profiles"]',
        translate(
          'accountManagerTour.routingProfilesTour.navigateToRoutingProfilesTitle'
        ),
        translate(
          'accountManagerTour.routingProfilesTour.navigateToRoutingProfilesBody'
        ),
        translate('accountManagerTour.routingProfilesTour.gotIt'),
        translate('accountManagerTour.routingProfilesTour.skip')
      ),
      new TourStopPoi(
        '[data-tour-poi="addRoutingProfileButton"]',
        translate(
          'accountManagerTour.routingProfilesTour.createRoutingProfileTitle'
        ),
        translate(
          'accountManagerTour.routingProfilesTour.createRoutingProfileBody'
        ),
        {
          nextButton: {
            displayText: translate(
              'accountManagerTour.routingProfilesTour.gotIt'
            ),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate(
              'accountManagerTour.routingProfilesTour.skip'
            ),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
    ]
  );
}

export function createMapSetTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.mapSetTour.itineraryTitle'),
    [
      createTourStopNavPoi(
        tourService,
        'ams/cfs/map-sets',
        '[data-tour-poi="cfs/map-sets"]',
        translate('accountManagerTour.mapSetTour.navigateToMapSetsTitle'),
        translate('accountManagerTour.mapSetTour.navigateToMapSetsBody'),
        translate('accountManagerTour.mapSetTour.gotIt'),
        translate('accountManagerTour.mapSetTour.skip')
      ),
      new TourStopPoi(
        '[data-tour-poi="addMapSetButton"]',
        translate('accountManagerTour.mapSetTour.createMapSetTitle'),
        translate('accountManagerTour.mapSetTour.createMapSetBody'),
        {
          nextButton: {
            displayText: translate('accountManagerTour.mapSetTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.mapSetTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
      new TourStopPoi(
        '[data-tour-poi="assignMapSetTable"]',
        translate('accountManagerTour.mapSetTour.assignToVehicleGroupTitle'),
        translate('accountManagerTour.mapSetTour.assignToVehicleGroupBody'),
        {
          nextButton: {
            displayText: translate('accountManagerTour.mapSetTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.mapSetTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        },
        { side: 'bottom', align: 'start' }
      ),
    ]
  );
}

export function createInviteDriversTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.inviteDriversTour.itineraryTitle'),
    [
      createTourStopNavPoi(
        tourService,
        '/ams/assets',
        '[data-tour-poi="assetsNav"]',
        translate('accountManagerTour.inviteDriversTour.navigateToAssetsTitle'),
        translate('accountManagerTour.inviteDriversTour.navigateToAssetsBody'),
        translate('accountManagerTour.inviteDriversTour.gotIt'),
        translate('accountManagerTour.inviteDriversTour.skip')
      ),
      new TourStopPoi(
        'app-invite-viewer',
        translate('accountManagerTour.inviteDriversTour.inviteContactTitle'),
        translate('accountManagerTour.inviteDriversTour.inviteContactBody'),
        {
          nextButton: {
            displayText: translate(
              'accountManagerTour.inviteDriversTour.gotIt'
            ),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.inviteDriversTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
    ]
  );
}

export function createAllTasksCompleteTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.allTasksCompleteTour.itineraryTitle'),
    [
      new TourStopPoi(
        'mop-tour-progress',
        translate('accountManagerTour.allTasksCompleteTour.helpButtonTitle'),
        translate('accountManagerTour.allTasksCompleteTour.helpButtonBody'),
        {
          nextButton: {
            displayText: translate(
              'accountManagerTour.allTasksCompleteTour.gotIt'
            ),
            listener: () => {
              tourService.nextStop();
            },
          },
        },
        { side: 'top', align: 'center' }
      ),
      new TourStopBillboard(
        allTasksCompleteBillboardTemplate,
        {
          title: translate('accountManagerTour.allTasksCompleteTour.title'),
          subtitle: translate(
            'accountManagerTour.allTasksCompleteTour.subtitle'
          ),
          card1Title: translate(
            'accountManagerTour.allTasksCompleteTour.card1Title'
          ),
          card1Text: translate(
            'accountManagerTour.allTasksCompleteTour.card1Text'
          ),
          card2Title: translate(
            'accountManagerTour.allTasksCompleteTour.card2Title'
          ),
          card2Text: translate(
            'accountManagerTour.allTasksCompleteTour.card2Text'
          ),
          card3Title: translate(
            'accountManagerTour.allTasksCompleteTour.card3Title'
          ),
          card3Text: translate(
            'accountManagerTour.allTasksCompleteTour.card3Text'
          ),
          card4Title: translate(
            'accountManagerTour.allTasksCompleteTour.card4Title'
          ),
          card4Text: translate(
            'accountManagerTour.allTasksCompleteTour.card4Text'
          ),
        },
        {
          nextButton: {
            displayText: translate(
              'accountManagerTour.allTasksCompleteTour.finish'
            ),
            listener: () => {
              tourService.endTour();
            },
          },
        }
      ),
    ],
    false
  );
}
